import React, { useState, useEffect, useCallback } from 'react';
import { ContestPhaseTab, IContestPhaseTab, PhaseData } from './ContestPhaseTab';
import { Box, Button, FormLabel, IconButton, Stack, Typography, Alert } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { useThemeContext } from '../../hook/useThemeManager';
import dayjs from 'dayjs';
import { ColorScheme, theme } from '../../constants/theme';
import AddIcon from '@mui/icons-material/Add';
import { GenericModal } from '../GenericModal/GenericModal';
import { CustomDatePicker } from '../DatePicker/CustomDatePicker';
import { StyleSheet } from 'react-native';
import { MainTagCarousel } from '../EsCarousels/MainTagCarousel';
import { showToastMessage } from '../../utils/AppUtils';
import {
    editorialHeadlineSx,
    editorialLabelSx,
    midnight,
} from '../../pages/BrandDashboard/contestWizard/midnightStyles';

type Phase = {
    phase: string;
    date_started: string;
    expiration_date: string;
};

interface IContestPhaseSelector extends Omit<IContestPhaseTab, 'data' | 'index'> {
    phases: PhaseData[];
    minStartDay?: string;
    minEndDay?: string;
    onChange?: (arg: Phase) => void;
    onUpdatePhase?: (arg: Phase, index: number) => void;
    onValidationError?: (error: string) => void;
    disableVotingPhase?: boolean;
    readOnly?: boolean;
    visualVariant?: 'default' | 'brand';
}

let CONTEST_PHASE = ['submission', 'ideation', 'pitch', 'vote', 'production', 'judging', 'event'];

const ContestPhaseSelector: React.FC<IContestPhaseSelector> = ({
    phases,
    onRemovePhase,
    onChange,
    onUpdatePhase,
    onValidationError,
    minStartDay,
    minEndDay,
    disableVotingPhase,
    readOnly,
    visualVariant = 'default',
}) => {
    const { themeStyle, device } = useThemeContext();
    const isBrand = visualVariant === 'brand';
    const [open, setOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [validationError, setValidationError] = useState<string>('');
    const [selectedPhase, setSelectedPhase] = useState<string>('');
    const styles = dynamicStyles(themeStyle);
    const [value, setValue] = useState({
        phase: '',
        date_started: '',
        expiration_date: '',
    });

    if (disableVotingPhase) {
        CONTEST_PHASE = CONTEST_PHASE.filter(phase => phase !== 'vote');
    }

    // Validate that all phases are in chronological order
    const validateAllPhases = useCallback((): string => {
        if (phases.length <= 1) return ''; // No validation needed for 0 or 1 phase

        const sortedPhases = sortPhasesChronologically(phases);

        for (let i = 0; i < sortedPhases.length - 1; i++) {
            const currentPhase = sortedPhases[i];
            const nextPhase = sortedPhases[i + 1];

            if (dayjs(currentPhase.expiration_date).isAfter(dayjs(nextPhase.date_started))) {
                return `Phase "${currentPhase.phase}" ends after "${nextPhase.phase}" starts`;
            }
        }

        return '';
    }, [phases]);

    // Validate all phases whenever phases change
    useEffect(() => {
        if (phases.length > 1) {
            const error = validateAllPhases();
            if (error && onValidationError) {
                onValidationError(error);
            }
        }
    }, [phases, onValidationError, validateAllPhases]);

    const handleOnChange = (field: string, value: string) => {
        setValue(prevValue => ({
            ...prevValue,
            [field]: value,
        }));
        setValidationError(''); // Clear validation error when user makes changes

        // Update selected phase when phase field changes
        if (field === 'phase') {
            setSelectedPhase(value);
        }
    };

    const validatePhase = (newPhase: Phase, excludeIndex?: number | null): string => {
        const startDate = dayjs(newPhase.date_started);
        const endDate = dayjs(newPhase.expiration_date);

        // Check if start date is before end date
        if (startDate.isAfter(endDate)) {
            return 'Start date must be before end date';
        }

        // Check for overlaps with existing phases
        for (let i = 0; i < phases.length; i++) {
            if (excludeIndex !== undefined && excludeIndex !== null && i === excludeIndex) continue;

            const existingPhase = phases[i];
            const existingStart = dayjs(existingPhase.date_started);
            const existingEnd = dayjs(existingPhase.expiration_date);

            // Check if new phase overlaps with existing phase
            if (
                (startDate.isBefore(existingEnd) && endDate.isAfter(existingStart)) ||
                startDate.isSame(existingStart) ||
                endDate.isSame(existingEnd) ||
                startDate.isSame(existingEnd) ||
                endDate.isSame(existingStart)
            ) {
                return `Phase "${existingPhase.phase}" overlaps with this time period`;
            }
        }

        // Check if phases are in chronological order
        const sortedPhases = [...phases]
            .filter((_, i) => excludeIndex !== undefined && excludeIndex !== null && i !== excludeIndex)
            .sort((a, b) => dayjs(a.date_started).valueOf() - dayjs(b.date_started).valueOf());

        if (sortedPhases.length > 0) {
            // Find the phase that should come before this one
            let previousPhase: PhaseData | null = null;
            for (const phase of sortedPhases) {
                if (dayjs(phase.date_started).isAfter(startDate)) {
                    break;
                }
                previousPhase = phase;
            }

            if (previousPhase && dayjs(previousPhase.expiration_date).isAfter(startDate)) {
                return `This phase should start after "${previousPhase.phase}" ends`;
            }
        }

        return '';
    };

    // Helper function to sort phases chronologically
    const sortPhasesChronologically = (phasesToSort: PhaseData[]): PhaseData[] => {
        return [...phasesToSort].sort((a, b) => dayjs(a.date_started).valueOf() - dayjs(b.date_started).valueOf());
    };

    const handleEditPhase = (phaseData: PhaseData, index: number) => {
        setValue({
            phase: phaseData.phase,
            date_started: phaseData.date_started,
            expiration_date: phaseData.expiration_date,
        });
        setSelectedPhase(phaseData.phase); // Set the selected phase for the carousel
        setEditingIndex(index);
        setOpen(true);
    };

    const onSubmit = () => {
        const error = validatePhase(value, editingIndex);
        if (error) {
            setValidationError(error);
            showToastMessage(error, 'error');
            return;
        }

        if (editingIndex !== null) {
            // Update existing phase
            onUpdatePhase && onUpdatePhase(value, editingIndex);
        } else {
            // Add new phase
            onChange && onChange(value);
        }

        // Reset form
        setValue({
            phase: '',
            date_started: '',
            expiration_date: '',
        });
        setEditingIndex(null);
        setSelectedPhase('');
        setValidationError('');
        setOpen(false);
    };

    const handleClose = () => {
        setOpen(false);
        setEditingIndex(null);
        setValue({
            phase: '',
            date_started: '',
            expiration_date: '',
        });
        setSelectedPhase('');
        setValidationError('');
    };

    // Get overall validation status for display
    const overallValidationError = validateAllPhases();

    return (
        <Stack direction="row" flexWrap="wrap" alignItems="center">
            {/* Show overall validation error if any */}
            {overallValidationError && (
                <Alert
                    severity="warning"
                    sx={
                        isBrand
                            ? {
                                  mb: 2,
                                  width: '100%',
                                  bgcolor: 'rgba(255,201,101,0.1)',
                                  color: midnight.onSurface,
                                  border: '1px solid rgba(255,201,101,0.25)',
                                  '& .MuiAlert-icon': { color: midnight.primary },
                              }
                            : { mb: 2, width: '100%' }
                    }>
                    {overallValidationError}
                </Alert>
            )}

            {phases.map((phase, index) => (
                <ContestPhaseTab
                    data={phase}
                    key={index}
                    onRemovePhase={phase => onRemovePhase && onRemovePhase(phase, index)}
                    onEditPhase={handleEditPhase}
                    index={index}
                    readOnly={readOnly}
                />
            ))}
            <GenericModal
                open={open}
                handleClose={handleClose}
                variant={isBrand ? 'brand' : 'default'}
                paperStyle={
                    isBrand
                        ? {
                              width: device.isDesktop ? 'min(760px, calc(100vw - 48px))' : '92%',
                              maxWidth: 760,
                          }
                        : { width: '100%' }
                }>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2.5,
                        width: '100%',
                        pb: isBrand ? 0 : 8,
                    }}>
                    <Typography
                        sx={
                            isBrand
                                ? {
                                      ...editorialHeadlineSx,
                                      fontSize: 22,
                                      color: midnight.onSurface,
                                      pr: 4,
                                  }
                                : {
                                      fontSize: 18,
                                      fontWeight: '600',
                                      fontFamily: FONT_ENUM.REGULAR_HEADER,
                                      color: theme[themeStyle].highlight,
                                      marginBottom: 2,
                                  }
                        }>
                        {editingIndex !== null ? 'Edit Phase' : 'Add New Phase'}
                    </Typography>

                    {validationError && (
                        <Alert
                            severity="error"
                            sx={
                                isBrand
                                    ? {
                                          bgcolor: 'rgba(255,115,81,0.12)',
                                          color: '#ff9a7a',
                                          border: '1px solid rgba(255,115,81,0.35)',
                                          '& .MuiAlert-icon': { color: '#ff7351' },
                                      }
                                    : { mb: 2 }
                            }>
                            {validationError}
                        </Alert>
                    )}

                    <MainTagCarousel
                        carouselTitle="Select Phase Title"
                        editorialPill={isBrand}
                        titleStyle={
                            isBrand
                                ? {
                                      textTransform: 'none',
                                      letterSpacing: '0.04em',
                                      fontSize: 13,
                                      fontWeight: 600,
                                      color: midnight.onSurfaceVariant,
                                      marginBottom: '8px',
                                      lineHeight: 1.4,
                                  }
                                : {
                                      textAlign: 'left',
                                      fontSize: 14,
                                      fontStyle: 'normal',
                                      fontWeight: '400',
                                      fontFamily: FONT_ENUM.MAIN_REGULAR,
                                      lineHeight: 2.5,
                                      color: theme[themeStyle].form_label,
                                      marginBottom: '10px',
                                  }
                        }
                        list={CONTEST_PHASE.filter(tag =>
                            editingIndex !== null
                                ? phases[editingIndex].phase === tag || phases.every(e => e.phase !== tag)
                                : phases.every(e => e.phase !== tag),
                        )}
                        disableViewAll
                        onSelectTag={tag => handleOnChange('phase', tag)}
                        initialValue={selectedPhase}
                        disregardParams
                        style={{
                            marginBottom: isBrand ? 8 : 20,
                            display: 'block',
                            gap: isBrand ? 12 : 37,
                        }}
                        listStyle={{
                            flexWrap: 'wrap',
                            gap: '10px',
                            overflowX: 'visible',
                        }}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: 2,
                            width: '100%',
                        }}>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormLabel
                                sx={
                                    isBrand
                                        ? {
                                              ...editorialLabelSx,
                                              textTransform: 'none',
                                              color: midnight.onSurfaceVariant,
                                          }
                                        : undefined
                                }
                                style={isBrand ? undefined : styles.text}>
                                Start date
                            </FormLabel>
                            <CustomDatePicker
                                minDate={minStartDay ? dayjs(minStartDay) : dayjs()}
                                onChange={(e, timezone) => {
                                    handleOnChange('date_started', e);
                                    if (timezone) {
                                        handleOnChange('timezone', timezone);
                                    }
                                }}
                                withTime
                                value={value.date_started}
                                brandLuxe={isBrand}
                                sx={isBrand ? undefined : { backgroundColor: theme[themeStyle].form_gray }}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <FormLabel
                                sx={
                                    isBrand
                                        ? {
                                              ...editorialLabelSx,
                                              textTransform: 'none',
                                              color: midnight.onSurfaceVariant,
                                          }
                                        : undefined
                                }
                                style={isBrand ? undefined : styles.text}>
                                End date
                            </FormLabel>
                            <CustomDatePicker
                                onChange={(e, timezone) => {
                                    handleOnChange('expiration_date', e);
                                    if (timezone) handleOnChange('timezone', timezone);
                                }}
                                minDate={
                                    minEndDay && dayjs(minEndDay).isValid()
                                        ? dayjs(minEndDay)
                                        : value.date_started && dayjs(value.date_started).isValid()
                                        ? dayjs(value.date_started)
                                        : minStartDay && dayjs(minStartDay).isValid()
                                        ? dayjs(minStartDay)
                                        : dayjs()
                                }
                                withTime
                                value={value.expiration_date}
                                brandLuxe={isBrand}
                                sx={isBrand ? undefined : { backgroundColor: theme[themeStyle].form_gray }}
                            />
                        </Box>
                    </Box>

                    <Button
                        onClick={onSubmit}
                        disabled={!value.phase || !value.expiration_date || !value.date_started}
                        fullWidth={isBrand}
                        sx={
                            isBrand
                                ? {
                                      mt: 1,
                                      py: 1.5,
                                      borderRadius: 999,
                                      fontFamily: '"Plus Jakarta Sans", Inter, sans-serif',
                                      fontWeight: 800,
                                      fontSize: 14,
                                      textTransform: 'none',
                                      bgcolor: midnight.primary,
                                      color: '#392700',
                                      '&:hover': { bgcolor: '#ecaa00' },
                                      '&.Mui-disabled': {
                                          bgcolor: 'rgba(255,201,101,0.35)',
                                          color: 'rgba(57,39,0,0.5)',
                                      },
                                  }
                                : {
                                      textDecoration: 'none',
                                      backgroundColor: theme[themeStyle].primary_orange,
                                      color: theme[themeStyle].pure_black,
                                      textTransform: 'none',
                                      width: device.isMobile ? '100%' : '160px',
                                      marginTop: '10px',
                                      marginBottom: '10px',
                                      position: 'absolute',
                                      left: 0,
                                      right: 0,
                                      bottom: 20,
                                      height: '52px',
                                      borderRadius: 100,
                                      fontSize: 16,
                                      fontWeight: '600',
                                      fontFamily: FONT_ENUM.REGULAR_HEADER,
                                      '&:disabled': {
                                          backgroundColor: theme[themeStyle].primary_orange,
                                          color: theme[themeStyle].darkGray60,
                                      },
                                      '&:hover': {
                                          border: `2px solid ${theme[themeStyle].primary_orange}`,
                                          backgroundColor: theme[themeStyle].pure_black,
                                          color: theme[themeStyle].primary_orange,
                                      },
                                  }
                        }>
                        {editingIndex !== null ? 'Update Phase' : 'Add Phase'}
                    </Button>
                </Box>
            </GenericModal>
            {!readOnly && (
                <Box
                    sx={{
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                    <IconButton
                        color="primary"
                        aria-label="Add phase"
                        onClick={() => setOpen(true)}
                        sx={
                            isBrand
                                ? {
                                      borderRadius: '50%',
                                      border: `1px solid ${midnight.outline}`,
                                      ml: 1,
                                      bgcolor: midnight.surfaceHighest,
                                      '&:hover': { bgcolor: 'rgba(255,201,101,0.12)', borderColor: midnight.primary },
                                  }
                                : undefined
                        }
                        style={
                            isBrand
                                ? undefined
                                : {
                                      borderRadius: '50%',
                                      border: `1px solid ${theme[themeStyle].lightWhite}`,
                                      marginLeft: 8,
                                  }
                        }>
                        <AddIcon
                            sx={{
                                height: 20,
                                width: 20,
                                color: isBrand ? midnight.primary : theme[themeStyle].primary_orange,
                            }}
                        />
                    </IconButton>
                    <Typography
                        sx={{
                            fontFamily: isBrand ? '"Manrope", Inter, sans-serif' : FONT_ENUM.REGULAR_HEADER,
                            fontSize: isBrand ? 10 : 8,
                            fontWeight: isBrand ? 700 : 400,
                            letterSpacing: isBrand ? '0.08em' : undefined,
                            textTransform: isBrand ? 'uppercase' : undefined,
                            color: isBrand ? midnight.onSurfaceVariant : theme[themeStyle].tag_white,
                            marginTop: 1,
                        }}>
                        Add New Phase
                    </Typography>
                </Box>
            )}
        </Stack>
    );
};

const dynamicStyles = (colorScheme: ColorScheme) =>
    StyleSheet.create({
        text: {
            textAlign: 'left',
            fontSize: 14,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].form_label,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        flexColumn: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'center',
            height: 'fit-content',
        },
        detailsButton: {
            height: '52px',
            borderRadius: 100,
            fontSize: 16,
            fontWeight: '600',
            fontFamily: FONT_ENUM.REGULAR_HEADER,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 20,
            width: '100%',
        },
    });

export { ContestPhaseSelector };
